import { GlAlert, GlBadge, GlLoadingIcon, GlModal, GlIcon } from '@gitlab/ui';
import aiActionMutation from 'ee/graphql_shared/mutations/ai_action.mutation.graphql';
import aiResponseSubscription from 'ee/graphql_shared/subscriptions/ai_completion_response.subscription.graphql';
import { getDraft, updateDraft } from '~/lib/utils/autosave';
import { fetchPolicies } from '~/lib/graphql';
import { __, s__ } from '~/locale';
const MAX_AI_WAIT_TIME = 30000;
export default {
    name: 'AiGenerateIssueDescription',
    components: {
        GlAlert,
        GlBadge,
        GlLoadingIcon,
        GlModal,
        GlIcon
    },
    props: {
        resourceId: {
            type: String,
            required: true
        },
        userId: {
            type: String,
            required: true
        }
    },
    emits: [
        'content-generated'
    ],
    data () {
        const autosaveKey = `convert_description_prompts/${this.resourceId}`;
        return {
            loading: false,
            loadingText: '',
            visible: true,
            error: '',
            autosaveKey,
            description: getDraft(autosaveKey)
        };
    },
    apollo: {
        $subscribe: {
            // eslint-disable-next-line @gitlab/vue-no-undef-apollo-properties
            aiCompletionResponse: {
                query: aiResponseSubscription,
                skip () {
                    return !this.loading;
                },
                fetchPolicy: fetchPolicies.NO_CACHE,
                variables () {
                    return {
                        resourceId: this.resourceId,
                        userId: this.userId
                    };
                },
                async result ({ data }) {
                    const { errors = [], content } = data.aiCompletionResponse || {};
                    if (errors.length > 0) {
                        this.handleError(errors[0].message);
                    }
                    if (!content) {
                        return;
                    }
                    clearTimeout(this.timeout);
                    const description = `${content}\n\n***\n_Description was generated using AI_`;
                    this.$emit('content-generated', description);
                    this.visible = false;
                    this.loading = false;
                }
            }
        }
    },
    i18n: {
        label: s__('AI|Write a brief description and have AI fill in the details.'),
        actionCancel: {
            text: __('Cancel')
        },
        buttonTitle: s__('AI|Write a summary to fill out the selected issue template'),
        placeholder: s__('AI|For example: Organizations should be able to forecast into the future by using value stream analytics charts. This feature would help them understand how their metrics are trending.'),
        missingRequiredText: s__('AI|Description is required'),
        autocompleteButtonText: s__('AI|Autocomplete'),
        actionPrimary: __('Submit'),
        error: __('Failed to generate description'),
        experiment: __('Experiment'),
        modalTitle: s__('AI|Populate issue description'),
        modalWarning: s__('AI|The existing description will be replaced when you submit.')
    },
    computed: {
        actionPrimary () {
            return {
                attributes: {
                    variant: 'confirm',
                    disabled: this.loading
                },
                text: this.$options.i18n.actionPrimary
            };
        }
    },
    watch: {
        description (val) {
            updateDraft(this.autosaveKey, val);
        }
    },
    methods: {
        submit (bvModalEvent) {
            // don't close modal on submit
            bvModalEvent.preventDefault();
            clearTimeout(this.timeout);
            this.clearError();
            if (!this.description?.trim()) {
                this.handleError(this.$options.i18n.missingRequiredText);
                return;
            }
            this.loadingText = __('Loading');
            this.loading = true;
            this.$apollo.mutate({
                mutation: aiActionMutation,
                variables: {
                    input: {
                        generateDescription: {
                            resourceId: this.resourceId,
                            content: this.description
                        }
                    }
                }
            }).then(({ data: { aiAction } })=>{
                if (aiAction.errors.length > 0) {
                    this.handleError(aiAction.errors[0]);
                }
            }).catch(this.handleError);
            this.timeout = setTimeout(()=>{
                this.handleError(this.$options.i18n.error);
            }, MAX_AI_WAIT_TIME);
        },
        handleError (error) {
            this.loading = false;
            this.error = error;
            clearTimeout(this.timeout);
        },
        clearError () {
            this.error = '';
        }
    },
    convertDescriptionModalId: 'convert-description-modal-id'
};
